import {
	CreateReleaseScriptsApiArg,
	DownloadCustomLovsLovCustomAttributeLovApiArg,
	mddApiSliceGeneratedV2,
} from './mddApiSliceGeneratedV2'

export const mddApiV2 = mddApiSliceGeneratedV2
	.enhanceEndpoints({
		addTagTypes: [
			'Templates',
			'Tenant',
			'Views',
			'Mappings',
			'Secrets',
			'Releases',
			'Structure',
		],
		endpoints: {
			// Template endpoints
			createTemplate: {
				invalidatesTags: ['Templates'],
			},
			getAllTemplates: {
				providesTags: ['Templates'],
			},
			updateTemplate: {
				invalidatesTags: ['Templates'],
			},
			deleteTemplate: {
				invalidatesTags: ['Templates'],
			},
			getTemplate: {
				providesTags: ['Templates'],
			},
			postTemplateLock: {
				invalidatesTags: ['Templates'],
			},
			postTemplateUnlock: {
				invalidatesTags: ['Templates'],
			},
			postTemplatePush: {
				invalidatesTags: ['Templates'],
			},
			getTemplateHistory: {
				providesTags: ['Templates'],
			},
			getTemplateHistoryDetail: {
				providesTags: ['Templates'],
			},
			// Tenant endpoints
			getTenant: {
				providesTags: ['Tenant'],
			},
			updateTenant: {
				invalidatesTags: ['Tenant'],
			},
			// View endpoints — tag by structureId so AI/external refresh can target one view
			getStructureDetail: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			getViewOverview: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			getViewColumns: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			getViewSources: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			getViewSourcesData: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			getViewPermissions: {
				providesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			updateView: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			updateViewOverview: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			updateViewColumns: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			updateViewSourceItems: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			updateViewSourcesData: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			setViewPermission: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			deleteViewPermission: {
				invalidatesTags: (_result, _error, arg) => [
					{ type: 'Views', id: arg.structureId },
				],
			},
			// Mapping endpoints
			getStructureDetail1: {
				providesTags: ['Mappings'],
			},
			updateMapping: {
				invalidatesTags: ['Mappings'],
			},
			// Secret endpoints
			getSecrets: {
				providesTags: ['Secrets'],
			},
			getSecret: {
				providesTags: ['Secrets'],
			},
			createSecret: {
				invalidatesTags: ['Secrets'],
			},
			updateSecret: {
				invalidatesTags: ['Secrets'],
			},
			deleteSecret: {
				invalidatesTags: ['Secrets'],
			},
			// Release endpoints
			getSystemRelease: {
				providesTags: ['Releases'],
			},
			updateSystemRelease: {
				invalidatesTags: ['Releases'],
			},
			deleteSystemRelease: {
				invalidatesTags: ['Releases'],
			},
			getDeploymentPackages: {
				providesTags: ['Releases'],
			},
			createDeploymentPackage: {
				invalidatesTags: ['Releases'],
			},
			getReleaseStructures: {
				providesTags: ['Releases'],
			},
			getReleaseEnvironments: {
				providesTags: ['Releases'],
			},
			// Structure endpoints
			getStructureMetadata: {
				providesTags: ['Structure'],
			},
			getChildren: {
				providesTags: ['Structure'],
			},
			createStructure: {
				invalidatesTags: ['Structure'],
			},
			getStructureTree: {
				providesTags: ['Structure'],
			},
			lockStructure: {
				invalidatesTags: ['Structure'],
			},
			unLockStructure: {
				invalidatesTags: ['Structure'],
			},
			bulkLock: {
				invalidatesTags: ['Structure'],
			},
			bulkUnlock: {
				invalidatesTags: ['Structure'],
			},
			createReleaseScripts: {
				extraOptions: {
					responseHandler: (response: Response) => response.blob(),
				},
			},
		},
	})
	.injectEndpoints({
		endpoints: (builder) => ({
			createReleaseScripts: builder.mutation<
				string,
				CreateReleaseScriptsApiArg
			>({
				query: (args) => ({
					url: `/v2/dp/${args.dpId}/environment/${args.envCode}/scripts`,
					method: 'POST',
					body: args.massExportExternalConfigDto,
					responseHandler: (response: Response) => response.blob(),
				}),
			}),
			downloadCustomLovsLovCustomAttributeLov: builder.query<
				string,
				DownloadCustomLovsLovCustomAttributeLovApiArg
			>({
				query: (queryArg) => ({
					url: `/v2/lov/custom-attr-lov/export`,
					params: {
						search: queryArg.search,
					},
					responseHandler: (response: Response) => response.text(),
				}),
			}),
		}),
		overrideExisting: true,
	})

export const {
	// Template hooks
	useGetAllTemplatesQuery,
	useGetTemplateQuery,
	useCreateTemplateMutation,
	useUpdateTemplateMutation,
	useDeleteTemplateMutation,
	usePostTemplateLockMutation,
	usePostTemplateUnlockMutation,
	usePostTemplatePushMutation,
	useGetTemplateHistoryQuery,
	useGetTemplateHistoryDetailQuery,
	useLazyGetTemplateHistoryQuery,
	useLazyGetTemplateHistoryDetailQuery,
	// Tenant hooks
	useGetTenantQuery,
	useUpdateTenantMutation,
	useLazyGetTenantQuery,
	// View hooks
	useGetStructureDetailQuery,
	useUpdateViewMutation,
	useGetViewSourcesQuery,
	useUpdateViewSourceItemsMutation,
	useAddViewSourceItemMutation,
	useUpdateViewSourceItemMutation,
	useDeleteViewSourceItemMutation,
	useGetViewSourcesDataQuery,
	useUpdateViewSourcesDataMutation,
	useGetViewOverviewQuery,
	useUpdateViewOverviewMutation,
	useGetViewColumnsQuery,
	useUpdateViewColumnsMutation,
	useAddViewColumnMutation,
	useUpdateViewColumnMutation,
	useUpdateViewColumn1Mutation,
	useLazyGetStructureDetailQuery,
	// Mapping hooks
	useGetStructureDetail1Query,
	useUpdateMappingMutation,
	useGetMappingSourcesQuery,
	useUpdateMappingSourceItemsMutation,
	useAddMappingSourceItemMutation,
	useUpdateMappingSourceItemMutation,
	useDeleteMappingSourceItemMutation,
	useGetMappingSourcesDataQuery,
	useUpdateMappingSourcesDataMutation,
	useGetMappingOverviewQuery,
	useUpdateMappingOverviewMutation,
	useGetMappingOtherScriptsQuery,
	useUpdateMappingOtherScriptsMutation,
	useGetMappingColumnsQuery,
	useUpdateMappingColumnsMutation,
	useAddMappingColumnMutation,
	useUpdateMappingColumnMutation,
	useUpdateMappingColumn1Mutation,
	useLazyGetStructureDetail1Query,
	// Secret hooks
	useGetSecretsQuery,
	useGetSecretQuery,
	useCreateSecretMutation,
	useUpdateSecretMutation,
	useDeleteSecretMutation,
	useLazyGetSecretsQuery,
	useLazyGetSecretQuery,
	// Release hooks
	useGetSystemReleaseQuery,
	useUpdateSystemReleaseMutation,
	useDeleteSystemReleaseMutation,
	useGetDeploymentPackagesQuery,
	useCreateDeploymentPackageMutation,
	useGetReleaseStructuresQuery,
	useGetReleaseEnvironmentsQuery,
	useCreateReleaseEnvironmentMutation,
	useUpdateReleaseEnvironmentMutation,
	useDeleteReleaseEnvironmentMutation,
	useCreateReleaseScriptsMutation,
	useLazyDownloadCustomLovsLovCustomAttributeLovQuery,
	useCreateDeploymentsMutation,
	useCreateReleasePreviewMutation,
	usePushReleaseToGitMutation,
	useChangeReleaseStatusMutation,
	useAddStructureToReleaseMutation,
	useDeleteReleaseStructureMutation,
	useLazyGetSystemReleaseQuery,
	useLazyGetDeploymentPackagesQuery,
	useLazyGetReleaseStructuresQuery,
	useLazyGetReleaseEnvironmentsQuery,
	// Structure hooks
	useGetStructureMetadataQuery,
	useGetChildrenQuery,
	useCreateStructureMutation,
	useGetStructureTreeQuery,
	useLockStructureMutation,
	useUnLockStructureMutation,
	useGetStructureHistoryQuery,
	useCreateStructureHistoryMutation,
	useCreateSystemMutation,
	useUndeleteStructureMutation,
	useGetFullTreeQuery,
	useGetDeletedStructuresQuery,
	useGetParentSystemQuery,
	useGetStructureHistoryWithDeploymentQuery,
	usePrePushValidationQuery,
	useLazyGetStructureMetadataQuery,
	useLazyGetChildrenQuery,
	useLazyGetStructureTreeQuery,
	useLazyGetStructureHistoryQuery,
	useLazyGetFullTreeQuery,
	useLazyGetDeletedStructuresQuery,
	useLazyGetParentSystemQuery,
	useLazyGetStructureHistoryWithDeploymentQuery,
	useLazyPrePushValidationQuery,
	useGetStructuresQuery,
	useLazyGetStructuresQuery,
	// Bulk lock/unlock hooks
	useBulkLockMutation,
	useBulkUnlockMutation,
	// Diagram hooks
	useGetDataForDiagramQuery,
	useLazyGetDataForDiagramQuery,
	// Permission hooks
	useGetViewPermissionsQuery,
	useSetViewPermissionMutation,
	useDeleteViewPermissionMutation,
	useGetMappingPermissionsQuery,
	useSetMappingPermissionMutation,
	useDeleteMappingPermissionMutation,
	useAsyncStructurePreviewMutation,
	// Expectation catalog hooks
	useGetExpectationsQuery,
	useLazyGetExpectationsQuery,
	useGetProvidersQuery,
	useLazyGetProvidersQuery,
} = mddApiV2
